"use client";

import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { useTenant } from "@/src/contexts/TenantContext";
import { useServiceArea } from "@/src/hooks/serviceArea/useServiceArea";
import { getSelectedCoordsRaw, parseCoords } from "@/src/constant/serviceArea/serviceArea";
import {
  deliveryBadge,
  getStoreDeliveryLabel,
  positionKey,
  type OutletDelivery,
  type StoreDeliveryLabel,
} from "@/src/services/delivery/storeDeliveryTime.service";

export type { DeliveryTone, OutletDelivery } from "@/src/services/delivery/storeDeliveryTime.service";

/** Header dispatches these whenever the buyer switches or edits an address. */
function subscribe(onChange: () => void) {
  window.addEventListener("selected_city_changed", onChange);
  window.addEventListener("address_changed", onChange);
  window.addEventListener("storage", onChange);
  return () => {
    window.removeEventListener("selected_city_changed", onChange);
    window.removeEventListener("address_changed", onChange);
    window.removeEventListener("storage", onChange);
  };
}

/**
 * Per-outlet delivery badge for the buyer's currently selected address.
 *
 * The label itself comes from the product service, which owns the text — the
 * store's own delivery time, else the distance band — and knows how each store
 * delivers; see `storeDeliveryTime.service.ts`. This hook's job is the buyer
 * half: which address is selected, re-fetching when it changes, and showing the
 * store's own text even to a guest with no coordinates (a guest, or an address
 * saved without coordinates, gets no distance band — most first-time traffic).
 *
 * A storefront renders one outlet across dozens of product cards and every card
 * mounts this hook; the service batches their asks into one request and keeps
 * the answer per (outlet, coordinates rounded to ~100 m) for the page.
 *
 * The server snapshot is null on purpose: localStorage does not exist during
 * SSR, so the badge is absent from the server HTML and appears on hydration,
 * which keeps the markup mismatch-free. Same pattern as `useServiceArea`.
 */
export function useOutletDelivery(outletIdOverride?: string): OutletDelivery {
  const { outletId: contextOutletId } = useTenant();
  const outletId = outletIdOverride || contextOutletId;
  const { blocked } = useServiceArea(outletIdOverride);

  const rawCoords = useSyncExternalStore(subscribe, getSelectedCoordsRaw, () => null);
  const coords = useMemo(() => parseCoords(rawCoords), [rawCoords]);

  /* One key identifies "the answer for this outlet, from this address" (or from
   * no address). Null whenever we have no question to ask. Deriving the visible
   * value by matching against it — rather than clearing state when the address
   * changes — is what makes a stale label impossible: the moment the buyer
   * switches address the key changes, so the previous address's answer stops
   * matching and the badge goes quiet until the new one lands. Clearing in an
   * effect instead would leave the old label on screen for a frame, and leave it
   * there permanently if the new lookup failed. An unserviceable address needs no
   * label: "Not delivered to your area" outranks it. */
  const key = outletId && !blocked ? `${outletId}|${positionKey(coords) ?? ""}` : null;

  const [entry, setEntry] = useState<{ key: string; label: StoreDeliveryLabel | null } | null>(null);

  useEffect(() => {
    if (!key || !outletId) return;
    let cancelled = false;

    getStoreDeliveryLabel(outletId, coords).then((label) => {
      if (!cancelled) setEntry({ key, label });
    });

    return () => { cancelled = true; };
    // `key` already encodes outletId + coords.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const label = entry && entry.key === key ? entry.label : null;

  // An unserviceable address outranks everything (the same `blocked` signal the
  // add-to-cart guard uses); otherwise the product service's label, if any.
  return useMemo<OutletDelivery>(() => deliveryBadge({ blocked, label }), [label, blocked]);
}
